import { z } from 'zod';

/**
 * The Notification Gateway module's contracts.
 *
 * Credentials are never returned by the service — only an indicator that they
 * are configured. The form therefore collects fresh credentials on every edit
 * and the service replaces the encrypted blob only when a complete set is sent.
 */

const NAME_MAX = 128;
const ADDRESS_MAX = 320;
const SMTP_MAX_PORT = 65535;
const PRIORITY_MAX = 9999;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const HOST_PATTERN = /^[a-z0-9][a-z0-9.-]*$/iu;

export const CHANNEL = {
  EMAIL: 'EMAIL',
  SMS: 'SMS',
} as const;

export type Channel = (typeof CHANNEL)[keyof typeof CHANNEL];

export const PROVIDER = {
  SMTP: 'SMTP',
  SENDGRID: 'SENDGRID',
  SES: 'SES',
  TWILIO: 'TWILIO',
  VONAGE: 'VONAGE',
  AWS_SNS: 'AWS_SNS',
  INFOBIP: 'INFOBIP',
} as const;

export type ProviderType = (typeof PROVIDER)[keyof typeof PROVIDER];

export const CHANNEL_OPTIONS: readonly { value: string; label: string }[] = [
  { value: CHANNEL.EMAIL, label: 'Email' },
  { value: CHANNEL.SMS, label: 'SMS' },
];

export const EMAIL_PROVIDER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: PROVIDER.SMTP, label: 'SMTP' },
  { value: PROVIDER.SENDGRID, label: 'SendGrid' },
  { value: PROVIDER.SES, label: 'AWS SES' },
  { value: PROVIDER.INFOBIP, label: 'Infobip' },
];

export const SMS_PROVIDER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: PROVIDER.TWILIO, label: 'Twilio' },
  { value: PROVIDER.VONAGE, label: 'Vonage' },
  { value: PROVIDER.AWS_SNS, label: 'AWS SNS' },
  { value: PROVIDER.INFOBIP, label: 'Infobip' },
];

export const SMS_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'Transactional', label: 'Transactional' },
  { value: 'Promotional', label: 'Promotional' },
];

/**
 * One row in the gateway list.
 *
 * Only `id` is guaranteed, because the list endpoint may eventually honour
 * column projection. Every other field is treated as optional for the same
 * reason the properties row is: a hidden column must not break the parse.
 */
export const gatewayRow = z.object({
  id: z.string(),
  name: z.string().optional(),
  channel: z.string().optional(),
  provider: z.string().optional(),
  fromAddress: z.string().nullable().optional(),
  fromName: z.string().nullable().optional(),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
  priority: z.number().optional(),
  credentialsConfigured: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type GatewayRow = z.infer<typeof gatewayRow>;

/** The full gateway, as the detail screen and the edit form read it. */
export const gatewayDetail = z.object({
  id: z.string(),
  tenantId: z.string(),
  channel: z.string(),
  provider: z.string(),
  name: z.string(),
  fromAddress: z.string().nullable(),
  fromName: z.string().nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  priority: z.number(),
  credentialsConfigured: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type GatewayDetail = z.infer<typeof gatewayDetail>;

const baseGatewayFormInput = z.object({
  channel: z.string().min(1, 'Choose a channel'),
  provider: z.string().min(1, 'Choose a provider'),
  name: z
    .string()
    .trim()
    .min(1, 'Give this gateway a name')
    .max(NAME_MAX, `Keep the name under ${String(NAME_MAX)} characters`),
  fromName: z.string().max(NAME_MAX, `Keep the sender name under ${String(NAME_MAX)} characters`),
  fromAddress: z.string().max(ADDRESS_MAX),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  priority: z.string(),
  // SMTP
  smtpHost: z.string(),
  smtpPort: z.string(),
  smtpUsername: z.string(),
  smtpPassword: z.string(),
  smtpSecure: z.boolean(),
  // SendGrid
  sendgridApiKey: z.string(),
  // Amazon SES
  sesRegion: z.string(),
  sesAccessKeyId: z.string(),
  sesSecretAccessKey: z.string(),
  // Twilio
  twilioAccountSid: z.string(),
  twilioAuthToken: z.string(),
  twilioFromNumber: z.string(),
  // Vonage
  vonageApiKey: z.string(),
  vonageApiSecret: z.string(),
  // Amazon SNS
  snsSenderId: z.string(),
  snsRegion: z.string(),
  snsAccessKeyId: z.string(),
  snsSecretAccessKey: z.string(),
  snsSmsType: z.string(),
  snsWebhookSecret: z.string(),
  // Infobip
  infobipSenderId: z.string(),
  infobipApiBaseUrl: z.string(),
  infobipApiKey: z.string(),
  infobipWebhookSecret: z.string(),
});

export type GatewayFormInput = z.infer<typeof baseGatewayFormInput>;

function hasAny(values: GatewayFormInput, fields: readonly (keyof GatewayFormInput)[]): boolean {
  for (const field of fields) {
    const value = values[field];
    if (typeof value === 'string' && value !== '') return true;
    if (typeof value === 'boolean' && value) return true;
  }
  return false;
}

function requireFields(
  values: GatewayFormInput,
  ctx: z.RefinementCtx,
  fields: readonly { readonly key: keyof GatewayFormInput; readonly message: string }[],
): void {
  for (const { key, message } of fields) {
    const value = values[key];
    if (typeof value === 'string' && value === '') {
      ctx.addIssue({
        code: 'custom',
        path: [key],
        message,
      });
    }
  }
}

function requirePort(
  values: GatewayFormInput,
  ctx: z.RefinementCtx,
  portKey: keyof GatewayFormInput,
  label: string,
): void {
  const raw = values[portKey];
  if (typeof raw !== 'string') return;

  if (raw === '') {
    ctx.addIssue({
      code: 'custom',
      path: [portKey],
      message: `${label} is required`,
    });
    return;
  }

  const port = Number(raw);
  if (!Number.isInteger(port) || port < 1 || port > SMTP_MAX_PORT) {
    ctx.addIssue({
      code: 'custom',
      path: [portKey],
      message: `${label} must be a whole number between 1 and ${String(SMTP_MAX_PORT)}`,
    });
  }
}

function requirePriority(values: GatewayFormInput, ctx: z.RefinementCtx): void {
  const raw = values.priority;
  const parsed = raw === '' ? 0 : Number(raw);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > PRIORITY_MAX) {
    ctx.addIssue({
      code: 'custom',
      path: ['priority'],
      message: `Priority must be a whole number between 0 and ${String(PRIORITY_MAX)}`,
    });
  }
}

function requireEmailFromAddress(values: GatewayFormInput, ctx: z.RefinementCtx): void {
  if (values.channel !== CHANNEL.EMAIL) return;
  if (values.fromAddress === '') return;

  if (!EMAIL_PATTERN.test(values.fromAddress)) {
    ctx.addIssue({
      code: 'custom',
      path: ['fromAddress'],
      message: 'Enter a valid email address, or leave this blank to use only the sender name',
    });
  }
}

function validSmtpCredentials(values: GatewayFormInput, ctx: z.RefinementCtx): void {
  requireFields(values, ctx, [
    { key: 'smtpHost', message: 'SMTP host is required' },
    { key: 'smtpUsername', message: 'SMTP username is required' },
    { key: 'smtpPassword', message: 'SMTP password is required' },
  ]);
  requirePort(values, ctx, 'smtpPort', 'SMTP port');

  if (values.smtpHost !== '' && !HOST_PATTERN.test(values.smtpHost)) {
    ctx.addIssue({
      code: 'custom',
      path: ['smtpHost'],
      message: 'Host must look like a domain, such as smtp.sendgrid.net',
    });
  }
}

function validSendgridCredentials(values: GatewayFormInput, ctx: z.RefinementCtx): void {
  requireFields(values, ctx, [{ key: 'sendgridApiKey', message: 'SendGrid API key is required' }]);
}

function validSesCredentials(values: GatewayFormInput, ctx: z.RefinementCtx): void {
  requireFields(values, ctx, [
    { key: 'sesRegion', message: 'AWS region is required' },
    { key: 'sesAccessKeyId', message: 'Access key ID is required' },
    { key: 'sesSecretAccessKey', message: 'Secret access key is required' },
  ]);
}

function validTwilioCredentials(values: GatewayFormInput, ctx: z.RefinementCtx): void {
  requireFields(values, ctx, [
    { key: 'twilioAccountSid', message: 'Twilio account SID is required' },
    { key: 'twilioAuthToken', message: 'Twilio auth token is required' },
  ]);
}

function validVonageCredentials(values: GatewayFormInput, ctx: z.RefinementCtx): void {
  requireFields(values, ctx, [
    { key: 'vonageApiKey', message: 'Vonage API key is required' },
    { key: 'vonageApiSecret', message: 'Vonage API secret is required' },
  ]);
}

function validSnsCredentials(values: GatewayFormInput, ctx: z.RefinementCtx): void {
  requireFields(values, ctx, [
    { key: 'snsSenderId', message: 'Sender ID is required' },
    { key: 'snsRegion', message: 'AWS region is required' },
    { key: 'snsAccessKeyId', message: 'Access key ID is required' },
    { key: 'snsSecretAccessKey', message: 'Secret access key is required' },
    { key: 'snsSmsType', message: 'SMS type is required' },
  ]);
}

function validInfobipCredentials(values: GatewayFormInput, ctx: z.RefinementCtx): void {
  requireFields(values, ctx, [
    { key: 'infobipApiBaseUrl', message: 'API base URL is required' },
    { key: 'infobipApiKey', message: 'API key is required' },
  ]);

  if (values.infobipApiBaseUrl !== '' && !values.infobipApiBaseUrl.startsWith('https://')) {
    ctx.addIssue({
      code: 'custom',
      path: ['infobipApiBaseUrl'],
      message: 'API base URL should start with https://',
    });
  }
}

const CREDENTIAL_FIELDS: Record<ProviderType, readonly (keyof GatewayFormInput)[]> = {
  [PROVIDER.SMTP]: ['smtpHost', 'smtpPort', 'smtpUsername', 'smtpPassword', 'smtpSecure'],
  [PROVIDER.SENDGRID]: ['sendgridApiKey'],
  [PROVIDER.SES]: ['sesRegion', 'sesAccessKeyId', 'sesSecretAccessKey'],
  [PROVIDER.TWILIO]: ['twilioAccountSid', 'twilioAuthToken', 'twilioFromNumber'],
  [PROVIDER.VONAGE]: ['vonageApiKey', 'vonageApiSecret'],
  [PROVIDER.AWS_SNS]: [
    'snsSenderId',
    'snsRegion',
    'snsAccessKeyId',
    'snsSecretAccessKey',
    'snsSmsType',
    'snsWebhookSecret',
  ],
  [PROVIDER.INFOBIP]: [
    'infobipSenderId',
    'infobipApiBaseUrl',
    'infobipApiKey',
    'infobipWebhookSecret',
  ],
};

const VALIDATORS: Record<ProviderType, (values: GatewayFormInput, ctx: z.RefinementCtx) => void> = {
  [PROVIDER.SMTP]: validSmtpCredentials,
  [PROVIDER.SENDGRID]: validSendgridCredentials,
  [PROVIDER.SES]: validSesCredentials,
  [PROVIDER.TWILIO]: validTwilioCredentials,
  [PROVIDER.VONAGE]: validVonageCredentials,
  [PROVIDER.AWS_SNS]: validSnsCredentials,
  [PROVIDER.INFOBIP]: validInfobipCredentials,
};

function validateProviderCredentials(
  values: GatewayFormInput,
  ctx: z.RefinementCtx,
  mode: 'create' | 'edit',
): void {
  if (values.provider === '') {
    ctx.addIssue({
      code: 'custom',
      path: ['provider'],
      message: 'Choose a provider',
    });
    return;
  }

  if (!Object.values(PROVIDER).includes(values.provider as ProviderType)) {
    ctx.addIssue({
      code: 'custom',
      path: ['provider'],
      message: 'Choose a recognised provider',
    });
    return;
  }

  requirePriority(values, ctx);
  requireEmailFromAddress(values, ctx);

  const fields = CREDENTIAL_FIELDS[values.provider as ProviderType];

  if (mode === 'create') {
    VALIDATORS[values.provider as ProviderType](values, ctx);
    return;
  }

  // Edit: the service never returns credentials, so leaving every field blank
  // means "keep the existing ones". Filling any credential means the whole set
  // is replaced, so all fields for that provider must then be present.
  if (hasAny(values, fields)) {
    VALIDATORS[values.provider as ProviderType](values, ctx);
  }
}

export const gatewayFormInputCreate = baseGatewayFormInput.superRefine((values, ctx) => {
  validateProviderCredentials(values, ctx, 'create');
});

export const gatewayFormInputUpdate = baseGatewayFormInput.superRefine((values, ctx) => {
  validateProviderCredentials(values, ctx, 'edit');
});

/** What the create endpoint returns. */
export const createdGateway = z.object({ id: z.string() });

export type CreatedGateway = z.infer<typeof createdGateway>;
